import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Grid,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material'
import { Search, History, Schedule } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { iaService, ResultadoBuscaInteligente } from '@/services/iaService'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { tokens } from '@/theme'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'

/**
 * Sugestões do Figma (Busca com IA): lista ESTÁTICA e declarativa — consulta · contexto
 * usado · resultado esperado. Clicar preenche a busca e dispara. Nada aqui é dado
 * fabricado: são exemplos de consulta.
 *
 * VC-12: desde a Onda 0 a busca do consultor só alcança documentos das reservas DELE —
 * as sugestões do consultor falam das próprias reservas. O back-office (a página é de
 * TODOS no menu) mantém as sugestões gerais, sobre as reservas que o perfil acessa.
 */
export interface SugestaoBusca {
  consulta: string
  contexto: string
  resultado: string
}

export const SUGESTOES_BUSCA_CONSULTOR: SugestaoBusca[] = [
  {
    consulta: 'contrato social das minhas reservas',
    contexto: 'Contratos sociais e alterações anexados às suas reservas',
    resultado: 'Quem administra e assina por cada empresa, no trecho citado',
  },
  {
    consulta: 'balanço patrimonial mais recente das minhas reservas',
    contexto: 'Balanços e DREs das suas reservas',
    resultado: 'Trechos com a receita, o patrimônio e o período de cada balanço',
  },
  {
    consulta: 'certidões vencidas nas minhas reservas',
    contexto: 'Certidões anexadas às suas reservas',
    resultado: 'Documentos cujo trecho cita a data de validade',
  },
  {
    consulta: 'documentos de garantia das minhas reservas',
    contexto: 'Matrículas, laudos e propostas de garantia das suas reservas',
    resultado: 'Documentos que mencionam imóvel ou outro bem em garantia',
  },
]

export const SUGESTOES_BUSCA_BACKOFFICE: SugestaoBusca[] = [
  {
    consulta: 'certidões negativas com validade vencida',
    contexto: 'Certidões anexadas às reservas',
    resultado: 'Documentos cujo trecho cita a data de validade',
  },
  {
    consulta: 'balanço patrimonial com receita líquida',
    contexto: 'Balanços e DREs das reservas',
    resultado: 'Trechos com o valor da receita e o período',
  },
  {
    consulta: 'contrato social com cláusula de administração',
    contexto: 'Contratos sociais e alterações',
    resultado: 'Quem assina pela empresa, no trecho citado',
  },
  {
    consulta: 'garantias imobiliárias oferecidas',
    contexto: 'Propostas e documentos de garantia',
    resultado: 'Documentos que mencionam imóvel em garantia',
  },
]

const escapeRegExp = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Realça as palavras da consulta no trecho. Passada única com uma regex das palavras
 * (a versão anterior iterava palavra a palavra e entrava em loop com termo vazio — agora
 * que a consulta é a real, precisa aguentar qualquer entrada).
 */
const highlightQuery = (texto: string, query: string): React.ReactNode => {
  const palavras = query.trim().split(/\s+/).filter(Boolean)
  if (palavras.length === 0) return texto

  const regex = new RegExp(`(${palavras.map(escapeRegExp).join('|')})`, 'gi')
  const partes = texto.split(regex)
  if (partes.length === 1) return texto

  return partes.map((parte, i) =>
    i % 2 === 1 ? (
      <span key={i} style={{ fontWeight: 700, color: tokens.brandPrimary }}>
        {parte}
      </span>
    ) : (
      parte
    )
  )
}

/**
 * Um resultado de `POST /v1/ia/buscar`: título, relevância, trecho citado (com a consulta
 * realçada) e data do documento quando o backend a conhece. "Ver documento →" abre o
 * detalhe real em `/documentos/:id` — as ações que só mostravam um aviso saíram.
 */
const SearchResultCard: React.FC<{
  resultado: ResultadoBuscaInteligente
  consulta: string
  onAbrir: () => void
}> = ({ resultado, consulta, onAbrir }) => (
  <Card
    sx={{
      p: 2.5,
      mb: 2,
      border: '1px solid ' + tokens.divider,
      transition: 'all 0.3s ease',
      '&:hover': { boxShadow: 4, borderColor: 'primary.main' },
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {resultado.titulo}
    </Typography>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
      <Typography variant="caption" color="textSecondary">
        Relevância:
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, maxWidth: '200px' }}>
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, resultado.relevancia * 100))}
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: '35px' }}>
          {Math.round(resultado.relevancia * 100)}%
        </Typography>
      </Box>
    </Box>

    <Typography variant="body2" sx={{ color: 'textSecondary', lineHeight: 1.5, mb: 1, fontStyle: 'italic' }}>
      "...{highlightQuery(resultado.trecho ?? '', consulta)}..."
    </Typography>

    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
      {resultado.dataDocumento && (
        <Chip
          label={dayjs(resultado.dataDocumento).format('DD/MM/YYYY')}
          size="small"
          variant="outlined"
          icon={<Schedule fontSize="small" />}
        />
      )}
      <Box sx={{ flex: 1 }} />
      <Button size="small" variant="text" onClick={onAbrir} sx={{ fontWeight: 600 }}>
        Ver documento →
      </Button>
    </Stack>
  </Card>
)

const BuscaIAPage: React.FC = () => {
  const navigate = useNavigate()
  const consultor = ehConsultor(authStore((s) => s.usuario)?.permissoes)
  const sugestoes = consultor ? SUGESTOES_BUSCA_CONSULTOR : SUGESTOES_BUSCA_BACKOFFICE
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  // Buscas recentes só as desta sessão — sem lista fabricada (R01 #8).
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [consultaExecutada, setConsultaExecutada] = useState<string>('')
  const [searchResults, setSearchResults] = useState<ResultadoBuscaInteligente[]>([])
  const [erroBusca, setErroBusca] = useState<string | null>(null)
  const [estadoErroBusca, setEstadoErroBusca] = useState<'erro' | 'semPermissao'>('erro')
  const { enqueueSnackbar } = useSnackbar()

  const executarBusca = useCallback(
    async (consulta: string) => {
      if (!consulta.trim()) {
        enqueueSnackbar('Digite algo para buscar', { variant: 'warning' })
        return
      }

      setIsSearching(true)
      setHasSearched(true)
      setErroBusca(null)

      try {
        const inicio = performance.now()
        const resultados = await iaService.buscar({ query: consulta, topK: 20 })
        const elapsed = ((performance.now() - inicio) / 1000).toFixed(1)

        setSearchResults(resultados)
        setConsultaExecutada(consulta)
        setRecentSearches((atuais) => [consulta, ...atuais.filter((s) => s !== consulta)].slice(0, 4))
        enqueueSnackbar(`${resultados.length} resultado${resultados.length === 1 ? '' : 's'} em ${elapsed}s`, {
          variant: resultados.length ? 'success' : 'info',
        })
      } catch (e) {
        setSearchResults([])
        const msg = e instanceof Error ? e.message : 'Falha ao buscar'
        setErroBusca(msg)
        setEstadoErroBusca((e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro')
        enqueueSnackbar(msg, { variant: 'error' })
      } finally {
        setIsSearching(false)
      }
    },
    [enqueueSnackbar],
  )

  const handleSearch = useCallback(() => void executarBusca(searchQuery), [executarBusca, searchQuery])

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  /** Sugestão do Figma: preenche a busca e já pesquisa. */
  const usarSugestao = (consulta: string) => {
    setSearchQuery(consulta)
    void executarBusca(consulta)
  }

  const handleRemoveRecentSearch = (busca: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setRecentSearches(recentSearches.filter((s) => s !== busca))
  }

  const hasNoResults = hasSearched && searchResults.length === 0 && !isSearching

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', mx: 'auto' }}>
      <PageHeader
        title="Busca com IA"
        subtitle={
          consultor
            ? 'Pesquise em linguagem natural nos documentos das suas reservas'
            : 'Pesquise em linguagem natural nos documentos das reservas que o seu perfil acessa'
        }
      />

      {/* KPIs declarativos (Figma 26:485): o quadro usa estes cards como texto
          informativo — não são métricas; nenhum número é inventado aqui. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Fontes"
            valor="Documentos"
            legenda={consultor ? 'Das suas reservas' : 'Das reservas'}
            variante="escura"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* Consultor: verdadeiro desde a Onda 0 (busca escopada às reservas dele). */}
          {consultor ? (
            <KpiCard label="Escopo" valor="Sua carteira" legenda="Somente o que é seu" />
          ) : (
            <KpiCard label="Escopo" valor="Seu perfil" legenda="Conforme o seu acesso" />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Respostas" valor="Com fonte" legenda="Trechos citam o documento" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Privacidade" valor="Protegida" legenda="Nada sai da plataforma" />
        </Grid>
      </Grid>

      <Card sx={{ p: 3, mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar documentos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isSearching}
          inputProps={{ 'aria-label': 'Consulta em linguagem natural' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  sx={{ mr: -1 }}
                >
                  {isSearching ? 'Buscando...' : 'Buscar'}
                </Button>
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '1.05rem' } }}
        />
        {/* Filtros avançados saíram (Onda 8 / A16): tipo e datas não existem em
            `POST /v1/ia/buscar` — eram controles sem efeito. */}
      </Card>

      {!hasSearched && (
        <>
          <Card sx={{ p: 3, mb: 4 }} data-testid="sugestoes-busca">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Sugestões de busca
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.textSecondary, display: 'block', mb: 2 }}>
              Consulta · contexto usado · resultado esperado. Clique para preencher a busca.
            </Typography>
            <Grid container spacing={1.5}>
              {sugestoes.map((s) => (
                <Grid item xs={12} sm={6} key={s.consulta}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea
                      onClick={() => usarSugestao(s.consulta)}
                      aria-label={`Buscar: ${s.consulta}`}
                      sx={{ p: 2, height: '100%', alignItems: 'flex-start' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.brandPrimary, mb: 1 }}>
                        "{s.consulta}"
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: tokens.textSecondary }}>
                        <strong>Contexto usado:</strong> {s.contexto}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: tokens.textSecondary }}>
                        <strong>Resultado esperado:</strong> {s.resultado}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Card>

          {recentSearches.length > 0 && (
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <History fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Buscas Recentes
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {recentSearches.map((busca) => (
                  <Chip
                    key={busca}
                    label={busca}
                    onClick={() => usarSugestao(busca)}
                    onDelete={(e) => handleRemoveRecentSearch(busca, e as React.MouseEvent)}
                    icon={<Schedule fontSize="small" />}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Card>
          )}
        </>
      )}

      {hasSearched && (
        <Box>
          {isSearching ? (
            <EstadoDeLista estado="carregando" mensagem="Buscando nos seus documentos…" />
          ) : erroBusca ? (
            <EstadoDeLista
              estado={estadoErroBusca}
              {...(estadoErroBusca === 'erro' && {
                mensagem: 'Não foi possível concluir a busca',
                detalhe: erroBusca,
                acao: { label: 'Tentar novamente', onClick: () => void executarBusca(searchQuery) },
              })}
            />
          ) : hasNoResults ? (
            <EstadoDeLista
              estado="vazio"
              mensagem={
                consultor
                  ? 'Nenhum trecho encontrado nos documentos das suas reservas.'
                  : 'Nenhum documento encontrado'
              }
              detalhe={`Nada foi encontrado para "${consultaExecutada || searchQuery}". Verifique a ortografia ou use menos termos.`}
              acao={{
                label: 'Nova busca',
                onClick: () => {
                  setHasSearched(false)
                  setSearchQuery('')
                },
              }}
            />
          ) : (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>{searchResults.length}</strong> resultado(s) encontrado(s)
                </Typography>
              </Box>
              {searchResults.map((resultado) => (
                <SearchResultCard
                  key={resultado.documentoId}
                  resultado={resultado}
                  consulta={consultaExecutada}
                  onAbrir={() => navigate(`/documentos/${resultado.documentoId}`)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default BuscaIAPage
